import { Box, HStack, MenuItem, Text } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { fundFamilyDateSchema } from "features/fund-family/FundFamilySchemas";
import { FundFamilySetupTabProps } from "features/fund-family/setup/FundFamilySetupTabProps";
import {
  useFundFamilyDatesCreate,
  useFundFamilyDatesRetrieveSuspense,
} from "gen/endpoints/dates/dates";
import { FundFamilyDateData } from "gen/model/fundFamilyDateData";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { FormDrawer } from "shared/components/form/FormDrawer";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { formatDateRange } from "shared/utils/DateUtils";
import { useBoolean } from "usehooks-ts";

interface FormDateRangeProps {
  title: string;
  description?: string;
}

const FormDateRange: React.FC<React.PropsWithChildren<FormDateRangeProps>> = ({
  title,
  description,
  children,
}) => (
  <Box
    _notLast={{
      mb: "20px",
    }}
  >
    <Text textStyle="overline">{title}</Text>
    {description && (
      <Text textStyle="small" maxWidth="560px" mt="8px">
        {description}
      </Text>
    )}
    <HStack
      mt="14px"
      spacing="16px"
      maxWidth="820px"
      alignItems="flex-start"
      sx={{
        "& > *": {
          flex: 1,
        },
      }}
    >
      {children}
    </HStack>
  </Box>
);

export const FundFamilySetupFundTabDates: React.FC = () => {
  const { t } = useTranslation(["fundFamily", "common"]);

  const { fundFamilyId } = useOutletContext<FundFamilySetupTabProps>();

  const { data } = useFundFamilyDatesRetrieveSuspense(fundFamilyId);

  const {
    value: isEditing,
    setTrue: startEditing,
    setFalse: stopEditing,
  } = useBoolean();

  return (
    <PageTabBody>
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockHeader>
            <Restricted permission={PermissionTypeEnum.config}>
              <ContentBlockActions>
                <ContentBlockMenu>
                  <MenuItem onClick={startEditing}>{t("common:edit")}</MenuItem>
                </ContentBlockMenu>
              </ContentBlockActions>
            </Restricted>
          </ContentBlockHeader>
          <ContentBlockBody variant="descriptions">
            <Descriptions>
              <Descriptions.Item
                label={t("fundFamily:setup.dates.fundStartAndEnd")}
              >
                {formatDateRange(data.fund_start_date, data.fund_end_date)}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("fundFamily:setup.dates.commitmentPeriod")}
              >
                {formatDateRange(
                  data.commitment_start_date,
                  data.commitment_end_date,
                )}
              </Descriptions.Item>
              <Descriptions.Item
                label={t("fundFamily:setup.dates.investmentPeriod")}
              >
                {formatDateRange(
                  data.investment_start_date,
                  data.investment_end_date,
                )}
              </Descriptions.Item>
            </Descriptions>
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
      {isEditing && (
        <FundFamilySetupFundTabDatesFormDrawer
          fundFamilyId={fundFamilyId}
          onClose={stopEditing}
        />
      )}
    </PageTabBody>
  );
};

const FundFamilySetupFundTabDatesFormDrawer: React.FC<{
  fundFamilyId: string;
  onClose: () => void;
}> = ({ fundFamilyId, onClose }) => {
  const { t } = useTranslation(["fundFamily"]);

  const { data } = useFundFamilyDatesRetrieveSuspense(fundFamilyId);

  const { mutateAsync: createFundFamilyDates } = useFundFamilyDatesCreate();

  const form = useForm({
    // @ts-expect-error TODO: Convert to zod
    resolver: yupResolver(fundFamilyDateSchema),
    defaultValues: data,
    mode: "all",
  });

  return (
    <FormDrawer
      title={t("fundFamily:setup.dates.editTitle")}
      formProps={{
        form,
        onSubmit: async (data: FundFamilyDateData) => {
          await createFundFamilyDates({ fundFamilyId, data });
          return data;
        },
        onSuccess: (data: FundFamilyDateData) => form.reset(data),
      }}
      onCloseComplete={onClose}
    >
      <FormDateRange title={t("fundFamily:setup.dates.fundStartAndEnd")}>
        <FormDateInput
          name="fund_start_date"
          title={t("fundFamily:setup.dates.fundStartDate")}
          dependentFieldNames={["fund_end_date"]}
        />
        <FormDateInput
          name="fund_end_date"
          title={t("fundFamily:setup.dates.fundEndDate")}
          dependentFieldNames={["fund_start_date"]}
        />
      </FormDateRange>
      <FormDateRange
        title={t("fundFamily:setup.dates.commitmentPeriod")}
        description={t("fundFamily:setup.dates.commitmentPeriodDescription")}
      >
        <FormDateInput
          name="commitment_start_date"
          title={t("fundFamily:setup.dates.commitmentStartDate")}
          dependentFieldNames={["commitment_end_date"]}
        />
        <FormDateInput
          name="commitment_end_date"
          title={t("fundFamily:setup.dates.commitmentEndDate")}
          dependentFieldNames={["commitment_start_date"]}
        />
      </FormDateRange>
      <FormDateRange
        title={t("fundFamily:setup.dates.investmentPeriod")}
        description={t("fundFamily:setup.dates.investmentPeriodDescription")}
      >
        <FormDateInput
          name="investment_start_date"
          title={t("fundFamily:setup.dates.investmentStartDate")}
          helpText={t("fundFamily:setup.dates.investmentStartDateDescription")}
        />
        <FormDateInput
          name="investment_end_date"
          title={t("fundFamily:setup.dates.investmentEndDate")}
        />
      </FormDateRange>
    </FormDrawer>
  );
};
